import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { WasteSuggestions } from './WasteSuggestions';
import { calculateWasteReduction } from '../utils/wasteCalculator';
import { premiumServices, surfaceShapes } from '../data/customization';
import { OrderSummary } from './OrderSummary';
import { 
  Star, 
  Award, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Quote, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Truck,
  Wrench,
  Heart,
  Upload,
  FileText,
  Copy,
  Printer,
  Share2,
  Twitter,
  Linkedin,
  Mail,
  Phone,
  Calendar,
  Building2,
  User,
  MessageSquare,
  AlertCircle,
  Send,
  CheckCircle,
  Recycle
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
      staggerChildren: 0.1
    }
  },
  exit: { opacity: 0, y: -50 }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const benefits = [
  {
    icon: Star,
    title: 'Expert Craftsmanship',
    description: 'Handcrafted by master artisans'
  },
  {
    icon: Shield,
    title: 'Quality Guaranteed',
    description: '10-year warranty on all projects'
  },
  {
    icon: Clock,
    title: 'Dedicated Service',
    description: 'Personal project manager'
  },
  {
    icon: MessageCircle,
    title: 'Direct Communication',
    description: 'Direct line to our design team'
  }
];

export const InquiryForm: React.FC = () => {
  const { 
    customizationOptions, 
    selectedMaterial, 
    customizationPath,
    selectedMaterials, 
    setStep,
    setFormState 
  } = useCustomizationStore();
  const { currency, language } = useLocalizationStore();
  const [formState, setLocalFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    company: '',
    projectTimeline: 'flexible',
    preferredContact: 'email',
    budget: '',
    projectType: 'residential',
    includeMaterial: customizationPath === 'slab-only' ? 'yes' : 'no'
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const wasteReduction = customizationOptions ? calculateWasteReduction(customizationOptions) : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLocalFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProductSelect = (productType: string) => {
    // Handle product selection
    console.log('Selected product:', productType);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCustomProject && customizationPath === 'slab-only' && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    setIsSubmitted(true);
    setFormState(formState); // Save form state to global store
    setTimeout(() => {
      setStep('confirmation');
    }, 2000);
  };

  const handleBack = () => {
    if (customizationPath === 'slab-only') {
      setStep('purchase-path');
    }
  };

  const isCustomProject = customizationOptions?.type === 'custom-project';

  if (isSubmitted) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="min-h-screen bg-cream flex items-center justify-center p-8"
      >
        <div className="max-w-md w-full text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="mb-8"
          >
            <CheckCircle className="w-24 h-24 text-gold mx-auto" />
          </motion.div>
          <h2 className="text-4xl font-bold text-gold mb-4">
            Thank You for Your Interest
          </h2>
          <p className="text-xl text-black/70 mb-8">
            Our design team will review your project and contact you within 24 hours to discuss the details.
          </p>
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6">
            <p className="text-black/70">
              For immediate assistance, contact our premium service line:
            </p>
            <p className="text-2xl font-semibold text-gold mt-2">+1 (800) 123-4567</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="min-h-screen bg-cream py-8 sm:py-16 px-4 sm:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div variants={itemVariants} className="text-center mb-8 sm:mb-16">
          <h1 className="text-3xl sm:text-5xl font-bold text-gold mb-4 sm:mb-6">
            {isCustomProject ? 'Custom Design Request' : 'Bespoke Design Service'}
          </h1>
          <p className="text-lg sm:text-xl text-black/70 max-w-2xl mx-auto">
            {isCustomProject 
              ? 'Tell us about your dream project. Our expert team will help bring your vision to life.'
              : 'Transform your vision into reality with our premium custom design service.'}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16">
          <motion.div variants={itemVariants}>
            <OrderSummary showActions={false} className="mb-8" />

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 space-y-6">
                <h3 className="text-2xl font-semibold text-gold mb-6">Personal Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
                      <input
                        type="text"
                        name="name"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black placeholder-black/40 focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Company Name
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
                      <input
                        type="text"
                        name="company"
                        value={formState.company}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black placeholder-black/40 focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="Company Ltd."
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black placeholder-black/40 focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
                      <input
                        type="tel"
                        name="phone"
                        value={formState.phone}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black placeholder-black/40 focus:ring-2 focus:ring-gold focus:border-transparent"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 space-y-6">
                <h3 className="text-2xl font-semibold text-gold mb-6">Project Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Project Type
                    </label>
                    <div className="relative">
                      <select
                        name="projectType"
                        value={formState.projectType}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 px-4 text-black appearance-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="hospitality">Hospitality</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Budget Range
                    </label>
                    <div className="relative">
                      <select
                        name="budget"
                        value={formState.budget}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 px-4 text-black appearance-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="">Select budget range</option>
                        <option value="10k-25k">€10,000 - €25,000</option>
                        <option value="25k-50k">€25,000 - €50,000</option>
                        <option value="50k-100k">€50,000 - €100,000</option>
                        <option value="100k+">€100,000+</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Timeline
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
                      <select
                        name="projectTimeline"
                        value={formState.projectTimeline}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black appearance-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="urgent">Urgent (Within 2 weeks)</option>
                        <option value="month">Within a month</option>
                        <option value="quarter">Next 3 months</option>
                        <option value="flexible">Flexible</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black/70 mb-2">
                      Preferred Contact Method
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/40" />
                      <select
                        name="preferredContact"
                        value={formState.preferredContact}
                        onChange={handleChange}
                        className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black appearance-none focus:ring-2 focus:ring-gold focus:border-transparent"
                      >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black/70 mb-2">
                    Project Description
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-4 top-4 w-5 h-5 text-black/40" />
                    <textarea
                      name="message"
                      rows={6}
                      value={formState.message}
                      onChange={handleChange}
                      className="w-full bg-white/50 border border-gold/20 rounded-xl py-3 pl-12 pr-4 text-black placeholder-black/40 focus:ring-2 focus:ring-gold focus:border-transparent"
                      placeholder="Please describe your project in detail, including dimensions, materials, and any specific requirements."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black/70 mb-4">
                    Project Documents & References
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex items-center justify-center w-full border-2 border-dashed border-gold/20 rounded-xl p-6 cursor-pointer hover:border-gold/40 transition-colors"
                    >
                      <Upload className="w-6 h-6 mr-2 text-black/70" />
                      <span className="text-black/70">Upload files (optional)</span>
                    </label>
                  </div>
                  {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-white/50 rounded-lg p-3">
                          <span className="text-sm text-black/70 truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-black/40 hover:text-black/70"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gold text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-gold-dark transition-colors"
                >
                  Submit Project Request
                  <Send className="w-5 h-5 ml-2" />
                </motion.button>

                {customizationPath === 'slab-only' && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={handleBack}
                    className="w-full bg-white text-gold border-2 border-gold py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-gold/5 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" />
                    Back to Slab Selection
                  </motion.button>
                )}
              </div>
            </form>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-8">
            {wasteReduction && (
              <div className="bg-gradient-to-br from-gold/20 to-gold-dark/20 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-gold/10 rounded-xl">
                    <Recycle className="w-8 h-8 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-gold mb-1">Smart Material Optimization</h3>
                    <p className="text-black/70">Maximize your investment with our intelligent material usage suggestions</p>
                  </div>
                </div>
                <WasteSuggestions
                  wasteReduction={wasteReduction}
                  onProductSelect={handleProductSelect}
                />
              </div>
            )}

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gold mb-6">Why Choose Our Bespoke Service</h3>
              <div className="grid grid-cols-1 gap-6">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-4">
                    <div className="p-3 bg-gold/10 rounded-xl">
                      <benefit.icon className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gold text-lg">{benefit.title}</h4>
                      <p className="text-black/70">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-gold/10 to-gold-dark/10 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gold mb-4">Project Gallery</h3>
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1600607686527-6fb886090705?w=800"
                  alt="Luxury project"
                  className="rounded-lg"
                />
                <img
                  src="https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800"
                  alt="Premium design"
                  className="rounded-lg"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};